import { z } from "zod";

/**
 * 税の土台（T1、2026-10-11。../eigyouryoku/docs/14_税率と軽減税率.md、DB 20261011_1537_tax_point）
 *
 * 税区分は「何を売ったか」（区分の名前に税率を入れない）。税率は取引日でマスタ（tax_rate_rules）から引く＝コードに税率を書かない。
 * 消費税は適格請求書1枚につき (課税の別, 税率) ごとに1回だけ丸める（ルール1）。8% の食品と 8% の新聞は1行。
 * 立替金は 税抜の対価（subtotal）にも消費税にも入れず、total＝subtotal＋taxTotal＋reimbursementTotal。
 */

/** 税区分（新コード7つ）。明細の入力（LineInput.taxCategory）はこれだけを受ける＝古いコードは 400 */
export const TAX_CATEGORIES = ["standard", "reduced_food", "reduced_news", "export", "non_taxable", "out_of_scope", "reimbursement"] as const;
export const TaxCategory = z.enum(TAX_CATEGORIES);
export type TaxCategory = z.infer<typeof TaxCategory>;

/** T1 より前に出した行に残る古いコード（読むときに taxCategoryNorm で新コードに読み替える） */
export const LEGACY_TAX_CATEGORIES = ["standard_10", "reduced_8", "exempt"] as const;

/** 課税の別（DB の fn_tax_kind）。集計の表 document_tax_summaries は (版, 課税の別, 税率) で1行 */
export const TaxKind = z.enum(["taxable", "export", "non_taxable", "out_of_scope", "reimbursement"]);
export type TaxKind = z.infer<typeof TaxKind>;

/** 画面の名前（T2 の税区分の列・品目の税区分で使う） */
export const TAX_CATEGORY_LABEL: Record<TaxCategory, string> = {
  standard: "標準",
  reduced_food: "軽減（飲食料品）",
  reduced_news: "軽減（新聞）",
  export: "免税（輸出）",
  non_taxable: "非課税",
  out_of_scope: "不課税",
  reimbursement: "立替金",
};

const LEGACY_TO_NEW: Record<(typeof LEGACY_TAX_CATEGORIES)[number], TaxCategory> = {
  standard_10: "standard",
  reduced_8: "reduced_food",
  exempt: "export",
};

/** 古いコードを新コードに読み替える（DB の fn_tax_category_norm と同じ）。知らない値は standard（既定）にする */
export function taxCategoryNorm(c: string | null | undefined): TaxCategory {
  if (c && c in LEGACY_TO_NEW) return LEGACY_TO_NEW[c as keyof typeof LEGACY_TO_NEW];
  return (TAX_CATEGORIES as readonly string[]).includes(c ?? "") ? (c as TaxCategory) : "standard";
}

const KIND_OF: Record<TaxCategory, TaxKind> = {
  standard: "taxable",
  reduced_food: "taxable",
  reduced_news: "taxable",
  export: "export",
  non_taxable: "non_taxable",
  out_of_scope: "out_of_scope",
  reimbursement: "reimbursement",
};

/** 区分 → 課税の別（DB の fn_tax_kind と同じ。古いコードも受ける） */
export const taxKindOf = (c: string): TaxKind => KIND_OF[taxCategoryNorm(c)];

/** 軽減税率の対象の区分か（DB の fn_tax_is_reduced と同じ） */
export const taxIsReduced = (c: string): boolean => {
  const n = taxCategoryNorm(c);
  return n === "reduced_food" || n === "reduced_news";
};

/** 取引日（YYYY-MM-DD）。2019-10-01 より前は DB が TAX_RATE_NOT_FOUND で断る（400） */
export const TaxDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * 税率ごとの集計の1行（帳票の taxes[]）。kind・rate・reduced が1行を決め、category は代表の区分（食品と新聞が混ざれば reduced_food）。
 * 画面・紙はラベルを kind・rate・reduced から作る（区分の文字を見ない）
 */
export const TaxSummary = z.object({
  kind: TaxKind,
  /** 0.10 = 10% */
  rate: z.number(),
  /** 軽減税率の対象（紙の「軽減 8%」） */
  reduced: z.boolean(),
  category: TaxCategory,
  taxableBase: z.number().int(),
  taxAmount: z.number().int(),
});
export type TaxSummary = z.infer<typeof TaxSummary>;

/** 消費税の行か（立替金の行は消費税でないので、画面・紙の消費税の行に並べない。立替金は合計欄の消費税の下に「立替金 ○円」の1行＝T2） */
export const isTaxRow = (t: Pick<TaxSummary, "kind">): boolean => t.kind !== "reimbursement";

export const TAX_MSG = {
  rateNotFound: "取引日に有効な消費税率がありません（2019年10月1日より前の取引日は扱えません）",
  // ---- T2：画面と紙の文（docs/10 §5.1.1：説明は「？」へ） ----
  /** 明細の列の見出し・行の詳細・品目の欄 */
  category: "税区分",
  categoryHelp: "軽減（飲食料品・新聞）・非課税・免税（輸出）・不課税・立替金を選べます。標準は 10% です。",
  /** 取引日（頭の欄・行の詳細） */
  taxDate: "取引日",
  taxDateHelp: "税率は取引日（お届け・引渡しの日）で決まります。空のときは見積日（請求日）で計算します",
  /** 送料を軽減にしたとき（お知らせの1行。止めない） */
  shippingReduced: "送料は軽減税率の対象外です。送料込みにするときは商品の単価に含めます",
  /** 見積 → 請求書で税率が変わった（お知らせの1行） */
  changedFromQuote: "取引日が {date} のため、{category} は {to} で計算しています（見積は {from}）",
  /** 合計欄の立替金の行・明細の名称の後ろ */
  reimbursement: "立替金",
  reimbursementMark: "（立替金）",
  /** 明細表の下（軽減の行があるときだけ。明細表の部品の一部） */
  reducedNote: "※は軽減税率対象",
  /** 案件の数字の枠の「？」（★7） */
  groupFiguresHelp: "立替金は売上に入れず、請求済・未入金には入れています",
  /** 立替金の行の仕入れの欄の代わり（粗利に入れない） */
  noCostForReimbursement: "立替金は仕入れ・粗利に入れません",
} as const;

// ---------------------------------------------------------------------------
// 税の画面と紙（T2、2026-10-11。../eigyouryoku/docs/infra/T2_税の画面と紙_実装依頼.md）
// ---------------------------------------------------------------------------

/** 明細の「税区分」の列（PC の見積・請求書）の短い名前。区分の名前は業種で変えない */
export const TAX_CATEGORY_SHORT: Record<TaxCategory, string> = {
  standard: "標準",
  reduced_food: "軽減",
  reduced_news: "新聞",
  export: "輸出",
  non_taxable: "非課税",
  out_of_scope: "不課税",
  reimbursement: "立替",
};

/** 税率の表示（0.08 → "8%"。紙の quote-html.ts の pct と同じ丸め） */
export const taxRatePct = (rate: number): string => `${Math.round(rate * 10000) / 100}%`;

/**
 * 紙の税率ごとの内訳の名前（§1-6 ②）：「消費税（10%）」「消費税（軽減 8%）」（1% も同じ形）。
 * 免税（輸出）・非課税・不課税は名前だけ（対象額だけを出し、税額の欄は空）。立替金は内訳に並べない（isTaxRow）
 */
export const taxPaperLabel = (t: Pick<TaxSummary, "kind" | "rate" | "reduced">): string =>
  t.kind === "taxable"
    ? t.reduced
      ? `消費税（軽減 ${taxRatePct(t.rate)}）`
      : `消費税（${taxRatePct(t.rate)}）`
    : t.kind === "export"
      ? "免税"
      : t.kind === "non_taxable"
        ? "非課税"
        : "不課税";

/**
 * 画面（PC の見積・請求書の合計の欄）の内訳の名前。標準は今の画面と同じ「消費税 10%」、軽減は「消費税（軽減 8%）」、
 * 免税・非課税・不課税は名前だけ（紙と同じ）
 */
export const taxScreenLabel = (t: Pick<TaxSummary, "kind" | "rate" | "reduced">): string =>
  t.kind === "taxable" && !t.reduced ? `消費税 ${taxRatePct(t.rate)}` : taxPaperLabel(t);

/** 消費税として印字する（税額の欄を持つ）行か。免税・非課税・不課税は対象額だけ（税額の欄は空） */
export const taxHasAmount = (t: Pick<TaxSummary, "kind">): boolean => t.kind === "taxable";

const KIND_ORDER: Record<TaxKind, number> = { taxable: 0, export: 1, non_taxable: 2, out_of_scope: 3, reimbursement: 4 };

/**
 * 税率ごとの内訳の並び（§1-6 ②）：10% → 8% → 1% → 免税 → 非課税 → 不課税。立替金は外す（内訳に並べない）。
 * 同じ税率なら軽減でない方を先に（経過措置の T4 の備え）
 */
export function taxRowsInOrder<T extends Pick<TaxSummary, "kind" | "rate" | "reduced">>(taxes: readonly T[]): T[] {
  return taxes
    .filter(isTaxRow)
    .map((t, i) => ({ t, i }))
    .sort((a, b) => KIND_ORDER[a.t.kind] - KIND_ORDER[b.t.kind] || b.t.rate - a.t.rate || Number(a.t.reduced) - Number(b.t.reduced) || a.i - b.i)
    .map((x) => x.t);
}

/**
 * 紙の明細の名称の後ろに付ける印（§1-6 ①③）：軽減の行は「 ※」、立替金の行は「（立替金）」。ほか（標準・免税・非課税・不課税）は付けない。
 * 区分が無い（古い形の入力・雛形）は標準
 */
export function taxNameMark(category: string | null | undefined): string {
  if (!category) return "";
  const c = taxCategoryNorm(category);
  return c === "reduced_food" || c === "reduced_news" ? " ※" : c === "reimbursement" ? TAX_MSG.reimbursementMark : "";
}

/** 紙に軽減の行があるか（「※は軽減税率対象」を明細表の下に出すか） */
export const hasReducedLine = (lines: ReadonlyArray<{ taxCategory?: string | null | undefined }>): boolean =>
  lines.some((l) => !!l.taxCategory && taxIsReduced(l.taxCategory));

/**
 * 紙の形が T2 で変わる帳票か（軽減・免税・非課税・不課税・立替金の行がある）。標準だけの紙は1バイトも変わらないので、
 * 紙のレイアウトの版（保存するファイルの名前）はこれが true の紙だけ上げる＝出した標準だけの紙は作り直さない
 */
export const hasTaxPaperChange = (lines: ReadonlyArray<{ taxCategory?: string | null | undefined }>): boolean =>
  lines.some((l) => !!l.taxCategory && taxCategoryNorm(l.taxCategory) !== "standard");

/** 送料の言葉（§1-7 ①）。この言葉を名前に含む行が軽減なら、お知らせの1行を出す（止めない） */
export const SHIPPING_WORDS = ["送料", "配送料", "配達料", "運賃", "宅配", "クール便"] as const;

/** 送料の行を軽減にしていたらお知らせの文、そうでなければ null（§1-7 ①。contract の1か所＝PC・スマホで同じ） */
export function shippingReducedWarning(name: string | null | undefined, category: string | null | undefined): string | null {
  if (!name || !category || !taxIsReduced(category)) return null;
  return SHIPPING_WORDS.some((w) => name.includes(w)) ? TAX_MSG.shippingReduced : null;
}

/** 取引日の欄を出すかの判定の期間（帳票の日付から1年）。API の taxDateHint が使う */
export const TAX_DATE_HINT_DAYS = 365;

/**
 * 見積 → 請求書で税率が変わった（API の Invoice.taxChangedFromQuote）。date＝請求書の取引日（無ければ請求日）、
 * changes＝区分ごとの 見積の税率 → 請求書の税率
 */
export const TaxChangedFromQuote = z.object({
  date: TaxDate,
  changes: z.array(z.object({ category: TaxCategory, from: z.number(), to: z.number() })),
});
export type TaxChangedFromQuote = z.infer<typeof TaxChangedFromQuote>;

/** 見積 → 請求書の税率の違いのお知らせの文（§1-7 ②）。区分ごとに1文 */
export function taxChangedText(x: TaxChangedFromQuote, jdate: (iso: string) => string): string[] {
  return x.changes.map((c) =>
    TAX_MSG.changedFromQuote
      .replace("{date}", jdate(x.date))
      .replace("{category}", TAX_CATEGORY_LABEL[c.category])
      .replace("{to}", taxRatePct(c.to))
      .replace("{from}", taxRatePct(c.from)),
  );
}

